/**
 * RegionFilterMenu — dropdown listing every distinct `zone` in suburb_cache.
 *
 * Zones come from Supabase rather than the deals stream so the menu stays
 * useful even when only a subset of zones has live deals. Cached at module
 * scope for 5 minutes — zone list churn is rare (only changes when the
 * Suburb Sheet is re-imported, which is itself a rare admin action).
 */

import { useEffect, useMemo, useState } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Check, ChevronDown, MapPin } from 'lucide-react';
import { clsx } from 'clsx';
import { getSupabase } from '../../lib/supabase';

interface Props {
  selectedZone: string | null;
  onSelect: (zone: string | null) => void;
}

interface CacheEntry { ts: number; data: string[] }
const TTL_MS = 5 * 60_000;
let _cache: CacheEntry | null = null;

async function fetchZones(): Promise<string[]> {
  if (_cache && Date.now() - _cache.ts < TTL_MS) return _cache.data;
  const supabase = getSupabase();
  const { data, error } = await supabase
    .from('v9_suburb_cache')
    .select('zone')
    .not('zone', 'is', null);
  if (error || !data) {
    if (error) console.warn('[RegionFilterMenu] fetch zones failed:', error.message);
    return [];
  }
  const set = new Set<string>();
  for (const row of data as { zone: string | null }[]) {
    if (row.zone) set.add(row.zone);
  }
  const list = Array.from(set).sort();
  _cache = { ts: Date.now(), data: list };
  return list;
}

export function RegionFilterMenu({ selectedZone, onSelect }: Props) {
  const [zones, setZones] = useState<string[]>(() => _cache?.data ?? []);
  const [loading, setLoading] = useState(!_cache);

  useEffect(() => {
    let cancelled = false;
    void fetchZones().then((list) => {
      if (cancelled) return;
      setZones(list);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const label = useMemo(() => {
    if (!selectedZone) return 'All zones';
    return selectedZone;
  }, [selectedZone]);

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-3 py-1.5 text-xs font-medium text-neutral-200 hover:bg-white/[0.06]"
        >
          <MapPin className="h-3.5 w-3.5 text-neutral-500" />
          <span>{label}</span>
          <ChevronDown className="h-3 w-3 text-neutral-500" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={6}
          className="z-50 max-h-[320px] min-w-[200px] overflow-auto rounded-xl border border-white/10 bg-neutral-900/95 p-1 text-sm text-neutral-200 shadow-2xl backdrop-blur"
        >
          <DropdownMenu.Item
            onSelect={() => onSelect(null)}
            className={clsx(
              'flex cursor-pointer items-center justify-between rounded-lg px-2.5 py-1.5 outline-none',
              selectedZone === null ? 'bg-brand-600/15 text-brand-200' : 'hover:bg-white/[0.06]',
            )}
          >
            <span>All zones</span>
            {selectedZone === null && <Check className="h-3.5 w-3.5" />}
          </DropdownMenu.Item>
          <DropdownMenu.Separator className="my-1 h-px bg-white/10" />
          {loading && (
            <div className="px-2.5 py-2 text-xs text-neutral-500">Loading zones…</div>
          )}
          {!loading && zones.length === 0 && (
            <div className="px-2.5 py-2 text-xs text-neutral-500">
              No zones in suburb cache. Sync your Suburb Sheet from Settings.
            </div>
          )}
          {zones.map((zone) => (
            <DropdownMenu.Item
              key={zone}
              onSelect={() => onSelect(zone)}
              className={clsx(
                'flex cursor-pointer items-center justify-between rounded-lg px-2.5 py-1.5 outline-none',
                selectedZone === zone ? 'bg-brand-600/15 text-brand-200' : 'hover:bg-white/[0.06]',
              )}
            >
              <span>{zone}</span>
              {selectedZone === zone && <Check className="h-3.5 w-3.5" />}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
